import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  IconButton,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import Autocomplete from '@mui/material/Autocomplete';
import axiosInstance from '../../../utils/axios';
import { useSnackbar } from 'notistack';

interface RecipientDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: RecipientDetailsData) => void;
  initialData?: RecipientDetailsData;
}

export interface RecipientDetailsData {
  recipient: string;
  xu_ly_chinh_mrb: string;
  phoi_hop_mrb: string[];
  don_vi_xu_ly: string[];
  don_vi_xu_ly_cp: string[];
  don_vi_phoi_hop_chua_qlvb: Array<{chuc_nang: string, don_vi: string}>;
  description: string;
  ghi_chu: string;
}

interface NoiNhanItem {
  data_value: string;
  id: number;
  noi_nhan_name: string;
  text: string;
  value: string;
}

const RecipientDetailsDialog: React.FC<RecipientDetailsDialogProps> = ({
  open,
  onClose,
  onSave,
  initialData
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [noiNhanList, setNoiNhanList] = useState<NoiNhanItem[]>([]);
  const [donViXuLyList, setDonViXuLyList] = useState<NoiNhanItem[]>([]);
  const [donViXuLyCPList, setDonViXuLyCPList] = useState<NoiNhanItem[]>([]);
  const [donViChuaQLVBList, setDonViChuaQLVBList] = useState<NoiNhanItem[]>([]);
  
  const [formData, setFormData] = useState<RecipientDetailsData>({
    recipient: '',
    xu_ly_chinh_mrb: '',
    phoi_hop_mrb: [],
    don_vi_xu_ly: [],
    don_vi_xu_ly_cp: [],
    don_vi_phoi_hop_chua_qlvb: [],
    description: '',
    ghi_chu: ''
  });

  // Hàm lấy danh sách nơi nhận từ API
  const fetchNoiNhanList = async () => {
    try {
      const response = await axiosInstance.post('/noi-nhan', {
        noi_nhan_name: "MRB",
        use_paginate: false
      });

      if (response.data.code === 0) {
        setNoiNhanList(response.data.data.items || []);
      } else {
        setNoiNhanList([]);
      }
    } catch (error) {

      setNoiNhanList([]);
    }
  };

  // Hàm lấy danh sách đơn vị xử lý sở ban ngành quận huyện từ API
  const fetchDonViXuLyList = async () => {
    try {
      const response = await axiosInstance.post('/noi-nhan', {
        noi_nhan_name: "sobannganhquanhuyen",
        use_paginate: false
      });

      if (response.data.code === 0) {
        setDonViXuLyList(response.data.data.items || []);
      } else {
        setDonViXuLyList([]);
      }
    } catch (error) {

      setDonViXuLyList([]);
    }
  };

  // Hàm lấy danh sách đơn vị xử lý Chính phủ/Bộ ngành Trung ương từ API
  const fetchDonViXuLyCPList = async () => {
    try {
      const response = await axiosInstance.post('/noi-nhan', {
        noi_nhan_name: "chinhphubonganhtrunguong",
        use_paginate: false
      });

      if (response.data.code === 0) {
        setDonViXuLyCPList(response.data.data.items || []);
      } else {
        setDonViXuLyCPList([]);
      }
    } catch (error) {

      setDonViXuLyCPList([]);
    }
  };

  // Hàm lấy danh sách đơn vị chưa sử dụng QLVB tập trung từ API
  const fetchDonViChuaQLVBList = async () => {
    try {
      const response = await axiosInstance.post('/noi-nhan', {
        noi_nhan_name: "donvichuasudungqlvb",
        use_paginate: false
      });

      if (response.data.code === 0) {
        setDonViChuaQLVBList(response.data.data.items || []);
      } else {
        setDonViChuaQLVBList([]);
      }
    } catch (error) {

      setDonViChuaQLVBList([]);
    }
  };

  // Effect để fetch tất cả data khi modal mở
  useEffect(() => {
    if (open) {
      setLoading(true);
      Promise.all([
        fetchNoiNhanList(),
        fetchDonViXuLyList(),
        fetchDonViXuLyCPList(),
        fetchDonViChuaQLVBList()
      ]).finally(() => {
        setLoading(false);
      });
    }
  }, [open]);

  useEffect(() => {
    if (open && initialData) {
      setFormData(initialData);
    }
  }, [open, initialData]);

  const handleSave = () => {
    onSave(formData);
    onClose();
  };

  const handleTextFieldChange = (field: keyof RecipientDetailsData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      sx={{ 
        zIndex: 1400,
        '& .MuiDialog-paper': {
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ bgcolor: '#2e7d32', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Thông tin nơi nhận và xử lý
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ color: 'white' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2} sx={{ pt: '20px' }}>
          {/* Đơn vị xử lý Sở, Ban, Ngành */}
          <Grid item xs={12}>
            <Autocomplete
              fullWidth
              multiple
              options={donViXuLyList}
              getOptionLabel={(option) => option.value}
              value={donViXuLyList.filter(opt => formData.don_vi_xu_ly.includes(opt.text))}
              onChange={(_, newValue) => {
                setFormData({
                  ...formData,
                  don_vi_xu_ly: newValue.map(opt => opt.text)
                });
              }}
              componentsProps={{
                popper: {
                  style: { zIndex: 1500 },
                  sx: {
                    width: '880px !important',
                    maxWidth: 'calc(100vw - 64px) !important'
                  }
                }
              }}
              renderInput={(params) => (
                <TextField {...params} label="Đơn vị xử lý Sở, Ban, Ngành" placeholder="Bắt đầu gõ để tìm kiếm đơn vị..."
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 'auto',
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: '#2e7d32',
                      opacity: 0.8,
                    },
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box>
                    <Typography variant="body2" component="span">
                      {option.value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block' }}>
                      {option.text}
                    </Typography>
                  </Box>
                </li>
              )}
              isOptionEqualToValue={(option, value) => option.text === value.text}
              noOptionsText="Không tìm thấy đơn vị"
              loading={false}
              filterOptions={(options, { inputValue }) => {
                // Chỉ hiển thị khi bắt đầu gõ
                if (!inputValue || inputValue.trim() === '') {
                  return [];
                }
                const searchTerm = inputValue.toLowerCase();
                return options.filter(option =>
                  option.value.toLowerCase().includes(searchTerm) ||
                  option.text.toLowerCase().includes(searchTerm)
                );
              }}
            />
          </Grid>

          {/* Đơn vị phối hợp chưa QLVB */}
          <Grid item xs={12}>
            <Autocomplete
              fullWidth
              multiple
              options={donViChuaQLVBList}
              getOptionLabel={(option) => option.value}
              value={donViChuaQLVBList.filter(opt => 
                formData.don_vi_phoi_hop_chua_qlvb.some(item => item.don_vi === opt.value)
              )}
              onChange={(_, newValue) => {
                setFormData({
                  ...formData,
                  don_vi_phoi_hop_chua_qlvb: newValue.map(opt => ({
                    chuc_nang: 'Phối hợp xử lý',
                    don_vi: opt.value
                  }))
                });
              }}
              componentsProps={{
                popper: {
                  style: { zIndex: 1500 },
                  sx: {
                    width: '880px !important',
                    maxWidth: 'calc(100vw - 64px) !important'
                  }
                }
              }}
              renderInput={(params) => (
                <TextField {...params} label="Đơn vị phối hợp (chưa QLVB tập trung)" placeholder="Bắt đầu gõ để tìm kiếm đơn vị..."
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 'auto',
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: '#2e7d32',
                      opacity: 0.8,
                    },
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box>
                    <Typography variant="body2" component="span">
                      {option.value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block' }}>
                      {option.data_value}
                    </Typography>
                  </Box>
                </li>
              )}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              noOptionsText="Không tìm thấy đơn vị"
              loading={false}
              filterOptions={(options, { inputValue }) => {
                // Chỉ hiển thị khi bắt đầu gõ
                if (!inputValue || inputValue.trim() === '') {
                  return [];
                }
                const searchTerm = inputValue.toLowerCase();
                return options.filter(option =>
                  option.value.toLowerCase().includes(searchTerm) ||
                  option.data_value.toLowerCase().includes(searchTerm)
                );
              }}
            />
          </Grid>

          {/* Đơn vị xử lý Chính phủ/Bộ ngành */}
          <Grid item xs={12}>
            <Autocomplete
              fullWidth
              multiple
              options={donViXuLyCPList}
              getOptionLabel={(option) => option.value}
              value={donViXuLyCPList.filter(opt => formData.don_vi_xu_ly_cp.includes(opt.value))}
              onChange={(_, newValue) => {
                setFormData({
                  ...formData,
                  don_vi_xu_ly_cp: newValue.map(opt => opt.value)
                });
              }}
              componentsProps={{
                popper: {
                  style: { zIndex: 1500 },
                  sx: {
                    width: '880px !important',
                    maxWidth: 'calc(100vw - 64px) !important'
                  }
                }
              }}
              renderInput={(params) => (
                <TextField {...params} label="Đơn vị xử lý Chính phủ/Bộ ngành Trung ương" placeholder="Bắt đầu gõ để tìm kiếm đơn vị..."
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 'auto',
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: '#2e7d32',
                      opacity: 0.8,
                    },
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box>
                    <Typography variant="body2" component="span">
                      {option.value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block' }}>
                      {option.data_value}
                    </Typography>
                  </Box>
                </li>
              )}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              noOptionsText="Không tìm thấy đơn vị"
              loading={false}
              filterOptions={(options, { inputValue }) => {
                // Chỉ hiển thị khi bắt đầu gõ
                if (!inputValue || inputValue.trim() === '') {
                  return [];
                }
                const searchTerm = inputValue.toLowerCase();
                return options.filter(option =>
                  option.value.toLowerCase().includes(searchTerm) ||
                  option.data_value.toLowerCase().includes(searchTerm)
                );
              }}
            />
          </Grid>

          {/* Xử lý chính MRB */}
          <Grid item xs={12}>
            <Autocomplete
              fullWidth
              options={noiNhanList}
              getOptionLabel={(option) => option.value}
              value={noiNhanList.find(opt => opt.text === formData.xu_ly_chinh_mrb) || null}
              onChange={(_, newValue) => {
                setFormData({
                  ...formData,
                  xu_ly_chinh_mrb: newValue ? newValue.text : '',
                  // Xóa người xử lý chính khỏi danh sách phối hợp nếu có
                  phoi_hop_mrb: formData.phoi_hop_mrb.filter(item => item !== (newValue ? newValue.text : ''))
                });
              }}
              componentsProps={{
                popper: {
                  style: { zIndex: 1500 },
                  sx: {
                    width: '880px !important',
                    maxWidth: 'calc(100vw - 64px) !important'
                  }
                }
              }}
              renderInput={(params) => (
                <TextField {...params} label="Nơi nhận chính MRB" placeholder="Bắt đầu gõ để tìm kiếm..." 
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 'auto',
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: '#2e7d32',
                      opacity: 0.8,
                    },
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box>
                    <Typography variant="body2" component="span">
                      {option.value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block' }}>
                      {option.text}
                    </Typography>
                  </Box>
                </li>
              )}
              isOptionEqualToValue={(option, value) => option.text === value.text}
              noOptionsText="Không tìm thấy người xử lý"
              loading={false}
              filterOptions={(options, { inputValue }) => {
                // Chỉ hiển thị khi bắt đầu gõ
                if (!inputValue || inputValue.trim() === '') {
                  return [];
                }
                const searchTerm = inputValue.toLowerCase();
                return options.filter(option =>
                  option.value.toLowerCase().includes(searchTerm) ||
                  option.text.toLowerCase().includes(searchTerm)
                );
              }}
            />
          </Grid>

          {/* Phối hợp MRB */}
          <Grid item xs={12}>
            <Autocomplete
              fullWidth
              multiple
              options={noiNhanList.filter(option => option.text !== formData.xu_ly_chinh_mrb)}
              getOptionLabel={(option) => option.value}
              value={noiNhanList.filter(opt => formData.phoi_hop_mrb.includes(opt.text))}
              onChange={(_, newValue) => {
                setFormData({
                  ...formData,
                  phoi_hop_mrb: newValue.map(opt => opt.text)
                });
              }}
              disabled={!formData.xu_ly_chinh_mrb}
              componentsProps={{
                popper: {
                  style: { zIndex: 1500 },
                  sx: {
                    width: '880px !important',
                    maxWidth: 'calc(100vw - 64px) !important'
                  }
                }
              }}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Nơi nhận phối hợp MRB" 
                  placeholder={formData.xu_ly_chinh_mrb ? "Bắt đầu gõ để tìm kiếm..." : "Vui lòng chọn Xử lý chính MRB trước"}
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 'auto',
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: '#2e7d32',
                      opacity: 0.8,
                    },
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box>
                    <Typography variant="body2" component="span">
                      {option.value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block' }}>
                      {option.text}
                    </Typography>
                  </Box>
                </li>
              )}
              isOptionEqualToValue={(option, value) => option.text === value.text}
              noOptionsText="Không tìm thấy người phối hợp"
              loading={false}
              filterOptions={(options, { inputValue }) => {
                // Chỉ hiển thị khi bắt đầu gõ
                if (!inputValue || inputValue.trim() === '') {
                  return [];
                }
                const searchTerm = inputValue.toLowerCase();
                return options.filter(option =>
                  option.value.toLowerCase().includes(searchTerm) ||
                  option.text.toLowerCase().includes(searchTerm)
                );
              }}
            />
          </Grid>

          {/* Nội dung */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              maxRows={10}
              label="Nội dung gửi văn thư đi"
              value={formData.description}
              onChange={handleTextFieldChange('description')}
              autoComplete="description"
              name="description"
              sx={{
                '& .MuiInputBase-root': {
                  height: 'auto',
                  '& textarea': {
                    height: '100% !important',
                    width: '100% !important',
                    overflow: 'auto',
                    paddingTop: '4px',
                    paddingBottom: '4px'
                  }
                }
              }}
            />
          </Grid>

          {/* Ghi chú */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              maxRows={6}
              label="Ghi chú gửi văn thư đi"
              value={formData.ghi_chu}
              onChange={handleTextFieldChange('ghi_chu')}
              autoComplete="ghi_chu"
              name="ghi_chu"
              sx={{
                '& .MuiInputBase-root': {
                  height: 'auto',
                  '& textarea': {
                    height: '100% !important',
                    width: '100% !important',
                    overflow: 'auto',
                    paddingTop: '4px',
                    paddingBottom: '4px'
                  }
                }
              }}
            />
          </Grid>
        </Grid>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, borderTop: '1px solid #ddd' }}>
        <Button onClick={onClose} variant="outlined">Hủy</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Lưu
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecipientDetailsDialog;

